import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

/** Notice record as returned by `getNotices` / `getNotice`. */
export interface AdminNotice {
  noticeId: string;
  noticeType?: string | null;
  title: string;
  content: string;
  createdBy?: string | null;
  createdAt?: string | null;
  updatedAt?: string | null;
}

export interface NoticeFormValues {
  noticeType: string;
  title: string;
  content: string;
}

/**
 * Selectable 공지 types.
 *
 * These are stored verbatim in DynamoDB because the member-facing NoticePage
 * renders `notice.noticeType` straight into the badge, so they are Korean
 * display strings rather than i18n keys. Kept in sync with the backend's
 * `DEFAULT_NOTICE_TYPE` ('공지'), which is the fallback when none is supplied.
 */
export const NOTICE_TYPES = ['공지', '이벤트', '점검', '업데이트'] as const;

/** Mirrors NOTICE_LIMITS in backend/src/lambdas/rbac/graphql-resolvers.ts. */
const MAX_TITLE = 200;
const MAX_CONTENT = 10000;

interface NoticeFormModalProps {
  /** Notice being edited, or null when creating a new one. */
  notice: AdminNotice | null;
  isSubmitting: boolean;
  onSubmit: (values: NoticeFormValues) => Promise<void>;
  onClose: () => void;
}

/**
 * Create/edit dialog for a 공지사항.
 *
 * Validation deliberately mirrors the resolver (blank title/content rejected,
 * same length caps) so the admin gets immediate feedback instead of a round
 * trip that returns `success: false`. The server still validates — this is a
 * convenience layer, not the enforcement point.
 */
const NoticeFormModal: React.FC<NoticeFormModalProps> = ({
  notice,
  isSubmitting,
  onSubmit,
  onClose,
}) => {
  const { t } = useTranslation(['admin', 'common']);
  const isEdit = notice !== null;

  const [values, setValues] = useState<NoticeFormValues>({
    noticeType: notice?.noticeType || NOTICE_TYPES[0],
    title: notice?.title ?? '',
    content: notice?.content ?? '',
  });
  const [errors, setErrors] = useState<Partial<Record<keyof NoticeFormValues, string>>>({});

  // Re-seed when the caller swaps which notice is being edited without
  // unmounting the dialog.
  useEffect(() => {
    setValues({
      noticeType: notice?.noticeType || NOTICE_TYPES[0],
      title: notice?.title ?? '',
      content: notice?.content ?? '',
    });
    setErrors({});
  }, [notice]);

  // Escape closes the dialog, matching the × button.
  useEffect(() => {
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [onClose]);

  const handleChange = (field: keyof NoticeFormValues, value: string) => {
    setValues((prev) => ({ ...prev, [field]: value }));
    setErrors((prev) => ({ ...prev, [field]: undefined }));
  };

  const validate = (): boolean => {
    const next: Partial<Record<keyof NoticeFormValues, string>> = {};
    if (!values.title.trim()) {
      next.title = t('admin:notices.form.errorTitleRequired') as string;
    } else if (values.title.trim().length > MAX_TITLE) {
      next.title = t('admin:notices.form.errorTitleTooLong', { max: MAX_TITLE }) as string;
    }
    if (!values.content.trim()) {
      next.content = t('admin:notices.form.errorContentRequired') as string;
    } else if (values.content.trim().length > MAX_CONTENT) {
      next.content = t('admin:notices.form.errorContentTooLong', { max: MAX_CONTENT }) as string;
    }
    setErrors(next);
    return Object.keys(next).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (isSubmitting || !validate()) return;
    await onSubmit({
      noticeType: values.noticeType,
      title: values.title.trim(),
      content: values.content.trim(),
    });
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 p-4"
      role="dialog"
      aria-modal="true"
      aria-labelledby="notice-form-heading"
    >
      <div className="flex max-h-[90vh] w-full max-w-2xl flex-col rounded-xl bg-white shadow-xl">
        <div className="flex items-center justify-between border-b border-gray-100 px-6 py-4">
          <h2 id="notice-form-heading" className="text-lg font-bold text-gray-900">
            {isEdit
              ? t('admin:notices.form.editTitle')
              : t('admin:notices.form.createTitle')}
          </h2>
          <button
            type="button"
            onClick={onClose}
            aria-label={t('common:close') as string}
            className="text-2xl leading-none text-gray-400 transition-colors hover:text-gray-600"
          >
            &times;
          </button>
        </div>

        <form onSubmit={handleSubmit} className="flex min-h-0 flex-1 flex-col">
          <div className="flex-1 space-y-4 overflow-y-auto px-6 py-5">
            {/* Type */}
            <div>
              <label
                htmlFor="notice-type"
                className="mb-1.5 block text-sm font-medium text-gray-700"
              >
                {t('admin:notices.form.type')}
              </label>
              <select
                id="notice-type"
                value={values.noticeType}
                onChange={(e) => handleChange('noticeType', e.target.value)}
                className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
              >
                {NOTICE_TYPES.map((type) => (
                  <option key={type} value={type}>
                    {type}
                  </option>
                ))}
              </select>
            </div>

            {/* Title */}
            <div>
              <label
                htmlFor="notice-title"
                className="mb-1.5 block text-sm font-medium text-gray-700"
              >
                {t('admin:notices.form.titleLabel')}
              </label>
              <input
                id="notice-title"
                type="text"
                value={values.title}
                maxLength={MAX_TITLE}
                onChange={(e) => handleChange('title', e.target.value)}
                placeholder={t('admin:notices.form.titlePlaceholder') as string}
                aria-invalid={Boolean(errors.title)}
                className={`w-full rounded-lg border px-3 py-2 text-sm focus:outline-none focus:ring-1 ${
                  errors.title
                    ? 'border-red-400 focus:border-red-500 focus:ring-red-500'
                    : 'border-gray-300 focus:border-blue-500 focus:ring-blue-500'
                }`}
              />
              <div className="mt-1 flex items-center justify-between">
                {errors.title ? (
                  <p className="text-xs text-red-500">{errors.title}</p>
                ) : (
                  <span />
                )}
                <span className="text-xs text-gray-400">
                  {t('admin:notices.form.charCounter', {
                    current: values.title.length,
                    max: MAX_TITLE,
                  })}
                </span>
              </div>
            </div>

            {/* Content */}
            <div>
              <label
                htmlFor="notice-content"
                className="mb-1.5 block text-sm font-medium text-gray-700"
              >
                {t('admin:notices.form.contentLabel')}
              </label>
              <textarea
                id="notice-content"
                value={values.content}
                rows={12}
                maxLength={MAX_CONTENT}
                onChange={(e) => handleChange('content', e.target.value)}
                placeholder={t('admin:notices.form.contentPlaceholder') as string}
                aria-invalid={Boolean(errors.content)}
                className={`w-full resize-y rounded-lg border px-3 py-2 text-sm focus:outline-none focus:ring-1 ${
                  errors.content
                    ? 'border-red-400 focus:border-red-500 focus:ring-red-500'
                    : 'border-gray-300 focus:border-blue-500 focus:ring-blue-500'
                }`}
              />
              <div className="mt-1 flex items-center justify-between">
                {errors.content ? (
                  <p className="text-xs text-red-500">{errors.content}</p>
                ) : (
                  <span />
                )}
                <span className="text-xs text-gray-400">
                  {t('admin:notices.form.charCounter', {
                    current: values.content.length,
                    max: MAX_CONTENT,
                  })}
                </span>
              </div>
            </div>
          </div>

          <div className="flex justify-end gap-2 border-t border-gray-100 px-6 py-4">
            <button
              type="button"
              onClick={onClose}
              disabled={isSubmitting}
              className="rounded-lg border border-gray-300 px-4 py-2 text-sm font-medium text-gray-700 transition-colors hover:bg-gray-50 disabled:opacity-50"
            >
              {t('admin:notices.form.cancel')}
            </button>
            <button
              type="submit"
              disabled={isSubmitting}
              className="rounded-lg bg-blue-600 px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-blue-700 disabled:cursor-not-allowed disabled:opacity-50"
            >
              {isSubmitting
                ? t('admin:notices.form.submitting')
                : isEdit
                  ? t('admin:notices.form.saveEdit')
                  : t('admin:notices.form.saveCreate')}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default NoticeFormModal;
